import type ExcelJS from 'exceljs';

import { formatCurrency } from '../../common-utils/formatters';
import { setupStepOrder } from '../@types/Company';
import type { FilterableCompany } from './companyListFilters';
import { ROUTES } from './routes';

const THIN_BORDER = {
  top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
};

/** Cell styles of the company list exports (companies of our base, card holders). */
export const EXPORT_HEADER_STYLE = {
  font: { bold: true, color: { argb: 'FFFFFFFF' } },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
  alignment: { horizontal: 'center', vertical: 'middle' },
  border: THIN_BORDER,
};

export const EXPORT_CELL_STYLE = {
  alignment: { horizontal: 'left', vertical: 'middle' },
  border: THIN_BORDER,
};

export const downloadWorkbook = async(workbook: ExcelJS.Workbook, fileName: string): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

const SETUP_STEP_COUNT = setupStepOrder.filter((step) => step !== 'vigilanceAdditionalCopro').length;

/** Validated setup steps of a company, as "3/9". */
export const formatSetupSteps = (setupSteps: unknown): string => {
  if (!setupSteps) {return `0/${SETUP_STEP_COUNT}`;}
  if (Array.isArray(setupSteps)) {return `${setupSteps.filter(Boolean).length}/${SETUP_STEP_COUNT}`;}
  if (typeof setupSteps === 'object') {return `${Object.values(setupSteps).filter(Boolean).length}/${SETUP_STEP_COUNT}`;}
  return 'N/A';
};

/** A company of our base, as the company and client lists load it. */
export interface ExportableCompany extends FilterableCompany {
  id: number;
  setupSteps?: unknown;
}

export interface CompanyExportColumn<T extends ExportableCompany = ExportableCompany> {
  header: string;
  width: number;
  value: (company: T) => string;
  /** The value is a URL, written as a "Voir la fiche RGPD" link. */
  link?: boolean;
}

function safeJsonParse<T>(jsonString: string | null | undefined, defaultValue: T): T {
  if (!jsonString) {return defaultValue;}
  try {
    return JSON.parse(jsonString) as T;
  } catch (e) {
    console.error('Error parsing JSON :', e);
    return defaultValue;
  }
}

const FNAIM_SERVICES_ORDER = ['Syndic de copropriété', 'Administrateur de biens', 'Transaction Vente Achat', 'Location habitation'];

/** "['Location habitation', 'Syndic de copropriété']" → "Syndic de copropriété, Location habitation". */
const parseFnaimServices = (fnaimServices: string) => {
  if (!fnaimServices) {return '';}
  try {
    const cleanedString = fnaimServices
      .replaceAll('\'', '"')
      .replaceAll(/\s+/g, ' ')
      .trim();
    const services = safeJsonParse(cleanedString, []);

    if (!Array.isArray(services) || services.length === 0) {
      return '';
    }

    // Known services first, in their order, then the others alphabetically
    return services
      .filter(service => typeof service === 'string')
      .sort((a: string, b: string) => {
        const indexA = FNAIM_SERVICES_ORDER.indexOf(a);
        const indexB = FNAIM_SERVICES_ORDER.indexOf(b);
        if (indexA === -1 && indexB === -1) {
          return a.localeCompare(b);
        }
        if (indexA === -1) {return 1;}
        if (indexB === -1) {return -1;}
        return indexA - indexB;
      })
      .join(', ');
  } catch (e) {
    console.error('Error parsing FNAIM services :', e);
    // If parsing fails, try to extract content between brackets
    if (typeof fnaimServices === 'string') {
      const match = fnaimServices.match(/\[(.*)\]/);
      if (match) {
        return match[1].split(',')
          .map(s => s.trim().replaceAll(/['"]/g, ''))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
          .join(', ');
      }
    }
    return typeof fnaimServices === 'string' ? fnaimServices : '';
  }
};

/** "{'Représentants légaux': 'DupontAlexMartinSam'}" → "Dupont, Alex, Martin, Sam". */
const parseFnaimRepresentatives = (fnaimAdditionalInfo: string) => {
  if (!fnaimAdditionalInfo) {return '';}
  try {
    const cleanedString = fnaimAdditionalInfo
      .replaceAll('\'', '"')
      .replaceAll(/\s+/g, ' ')
      .trim();
    const info = safeJsonParse(cleanedString, {});
    if (!info['Représentants légaux']) {return '';}

    return info['Représentants légaux']
      .split(/(?<=[a-z])(?=[A-Z])/)
      .map((name: string) => name.trim())
      .filter((name: string) => name.length > 0)
      .join(', ');
  } catch (e) {
    console.error('Error parsing FNAIM representatives :', e);
    // If parsing fails, try to extract content after "Représentants légaux"
    if (typeof fnaimAdditionalInfo === 'string') {
      const match = fnaimAdditionalInfo.match(/Représentants légaux["\s]*:?["\s]*([^"}\]]+)/);
      if (match) {
        return match[1].trim();
      }
    }
    return '';
  }
};

const getSnpiServices = (company: ExportableCompany) => {
  if (!company.details) {return '';}
  const services = [];
  if (company.details.snpiAdministrator) {services.push('Administrateur de biens');}
  if (company.details.snpiManager) {services.push('Syndic');}
  if (company.details.snpiHouseAgent) {services.push('Agent immobilier');}
  if (company.details.snpiExpert) {services.push('Expert immobilier');}
  return services.join(', ');
};

const getGarantieFinanciere = (company: ExportableCompany) => {
  if (!company.details || !company.details.garants) {return '';}
  try {
    const garants = safeJsonParse(company.details.garants, []);
    if (!Array.isArray(garants) || garants.length === 0) {return '';}
    return garants
      .filter(g => g && typeof g === 'object')
      .map(g => `${g.nom || ''}: ${formatCurrency(parseFloat(g.montant_de_la_garantie || '0'))}`)
      .filter(Boolean)
      .join(', ');
  } catch (e) {
    console.error('Error parsing garants :', e);
    return '';
  }
};

const getAssureurs = (company: ExportableCompany) => {
  if (!company.details || !company.details.assureurs) {return '';}
  try {
    const assureurs = safeJsonParse(company.details.assureurs, []);
    if (!Array.isArray(assureurs) || assureurs.length === 0) {return '';}
    return assureurs
      .filter(a => a && typeof a === 'object')
      .map(a => a.assureur_nom || '')
      .filter(Boolean)
      .join(', ');
  } catch (e) {
    console.error('Error parsing assureurs :', e);
    return '';
  }
};

/** First legal representative of the SIRENE file. */
const parseRepresentantsLegaux = (representantsJson: string | null | undefined) => {
  if (!representantsJson) {return { nom: '', prenom: '' };}
  try {
    const representants = safeJsonParse(representantsJson, []);
    if (!Array.isArray(representants) || representants.length === 0) {return { nom: '', prenom: '' };}

    const firstRepresentant = representants[0];
    return {
      nom: firstRepresentant.nom || '',
      prenom: firstRepresentant.prenom || '',
    };
  } catch (e) {
    console.error('Error parsing representants_legaux :', e);
    return { nom: '', prenom: '' };
  }
};

const yesNo = (value: unknown): string => (value ? 'Oui' : 'Non');
const hasActivity = (company: ExportableCompany, activity: string): string => yesNo((company.activities || []).includes(activity));
const formatCount = (value: unknown): string => (Number(value) || 0).toLocaleString('fr-FR');
const details = (company: ExportableCompany) => company.details || {};

/** Columns of the company list export; the client list adds its subscription after the SIREN. */
export const COMPANY_EXPORT_COLUMNS: CompanyExportColumn[] = [
  { header: 'NOM', width: 30, value: (company) => company.name || '' },
  { header: 'SIREN', width: 20, value: (company) => company.siren || '' },
  { header: 'Membre FNAIM', width: 15, value: (company) => yesNo(details(company).fnaim) },
  { header: 'Membre UNIS', width: 15, value: (company) => yesNo(details(company).unis) },
  { header: 'Membre SNPI', width: 15, value: (company) => yesNo(details(company).snpi) },
  { header: 'Activité Syndic', width: 15, value: (company) => hasActivity(company, 'Syndic') },
  { header: 'Activité Gestion locative', width: 20, value: (company) => hasActivity(company, 'Gestion locative') },
  { header: 'Activité Transaction', width: 20, value: (company) => hasActivity(company, 'Transaction') },
  { header: 'Activité Location', width: 15, value: (company) => hasActivity(company, 'Location') },
  { header: 'NB LOTS HAB', width: 15, value: (company) => details(company).nbLotsHab?.toLocaleString('fr-FR') || '0' },
  { header: 'NB LOTS HAB / BUR / COM', width: 20, value: (company) => details(company).nbLotsHabBurCom?.toLocaleString('fr-FR') || '0' },
  { header: 'Nombre Copropriétés', width: 25, value: (company) => formatCount(details(company).nombreCopros) },
  { header: 'ÉTAPE CONFIG VALIDE', width: 20, value: (company) => formatSetupSteps(company.setupSteps) },
  { header: 'Activités FNAIM', width: 40, value: (company) => parseFnaimServices(details(company).fnaimServices ?? '') || '' },
  { header: 'Activités SNPI', width: 40, value: (company) => getSnpiServices(company) || '' },
  { header: 'Site FNAIM', width: 30, value: (company) => details(company).fnaimWebsite || '' },
  { header: 'Site SNPI', width: 30, value: (company) => details(company).snpiUrl || '' },
  { header: 'Téléphone FNAIM', width: 20, value: (company) => details(company).fnaimPhone || '' },
  { header: 'Téléphone SNPI', width: 20, value: (company) => details(company).snpiPhone || '' },
  { header: 'Représentants légaux FNAIM', width: 40, value: (company) => parseFnaimRepresentatives(details(company).fnaimAdditionalInfo ?? '') || '' },
  { header: 'Représentant légal SNPI', width: 40, value: (company) => details(company).snpiOwner || '' },
  {
    header: 'Date fin carte',
    width: 20,
    value: (company) => (details(company).dateFinCarte ? new Date(details(company).dateFinCarte as string).toLocaleDateString('fr-FR') : ''),
  },
  { header: 'Transaction CARTE', width: 20, value: (company) => yesNo(details(company).transactionPappers) },
  { header: 'Gestion Immobilière CARTE', width: 20, value: (company) => yesNo(details(company).gestionImmobilierePappers) },
  { header: 'Syndic CARTE', width: 20, value: (company) => yesNo(details(company).syndicPappers) },
  { header: 'Garantie Financière', width: 20, value: (company) => getGarantieFinanciere(company) || '' },
  {
    header: 'Garant',
    width: 20,
    value: (company) => (details(company).garants
      ? ((safeJsonParse(details(company).garants as string, []) as Array<{ nom?: string }>)[0]?.nom || '')
      : ''),
  },
  { header: 'Assureur RC Pro', width: 20, value: (company) => getAssureurs(company) || '' },
  { header: 'Banque Compte Séquestre', width: 20, value: (company) => String(details(company).banqueCompteSequestre || '') },
  { header: 'Carte', width: 20, value: (company) => details(company).carte || '' },
  { header: 'Info Dirigeant CARTE', width: 20, value: (company) => details(company).infoDirigeant || '' },
  { header: 'Nom Commercial', width: 30, value: (company) => String(details(company).nomCommercial || '') },
  {
    header: 'Adresse Siège',
    width: 40,
    value: (company) => {
      const { numeroAdresse, indiceRepetitionAdresse, typeAdresse, rueAdresse } = details(company);
      return `${String(numeroAdresse ?? '')}${String(indiceRepetitionAdresse ?? '')} ${String(typeAdresse ?? '')} ${String(rueAdresse ?? '')}`.trim() || '';
    },
  },
  { header: 'Code Postal Siège', width: 15, value: (company) => String(details(company).codePostal || '') },
  { header: 'Ville Siège', width: 25, value: (company) => String(details(company).ville || '') },
  { header: 'Nombre Établissements', width: 25, value: (company) => formatCount(details(company).nombreEtablissements) },
  { header: 'Nombre Établissements Actifs', width: 25, value: (company) => formatCount(details(company).nombreEtablissementsActifs) },
  {
    header: 'Nom Représentant Légal SIREN',
    width: 40,
    value: (company) => parseRepresentantsLegaux(String(details(company).representantsLegaux ?? '')).nom || '',
  },
  {
    header: 'Prénom Représentant Légal SIREN',
    width: 40,
    value: (company) => parseRepresentantsLegaux(String(details(company).representantsLegaux ?? '')).prenom || '',
  },
  {
    header: 'Lien fiche RGPD',
    width: 50,
    value: (company) => (company.id
      ? `${window.location.origin}${ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', company.id.toString())}`
      : ''),
    link: true,
  },
];

/** Header, one row per company, column widths, then the links. */
export const writeCompanyListSheet = <T extends ExportableCompany>(
  worksheet: ExcelJS.Worksheet,
  columns: CompanyExportColumn<T>[],
  companies: T[],
): void => {
  worksheet.addRow(columns.map((column) => column.header)).eachCell((cell) => Object.assign(cell, EXPORT_HEADER_STYLE));

  companies.forEach((company) => {
    let row: string[];
    try {
      row = columns.map((column) => column.value(company));
    } catch (error) {
      console.error('Error adding row for company :', company.id, error);
      row = [company.name || 'Unknown Company', company.siren || '', 'Error', ...Array(columns.length - 3).fill('')];
    }
    worksheet.addRow(row).eachCell((cell) => Object.assign(cell, EXPORT_CELL_STYLE));
  });

  worksheet.columns.forEach((column, index) => {
    column.width = columns[index]?.width || 20;
  });

  columns.forEach(({ link }, index) => {
    if (!link) {return;}
    worksheet.getColumn(index + 1).eachCell((cell, rowNumber) => {
      if (rowNumber === 1) {return;}
      if (typeof cell.value === 'string' && cell.value.trim() !== '') {
        cell.value = {
          text: 'Voir la fiche RGPD',
          hyperlink: cell.value,
          tooltip: 'Cliquez pour ouvrir la fiche RGPD',
        };
        cell.font = {
          color: { argb: 'FF0000FF' },
          underline: true,
        };
      } else {
        cell.value = '';
      }
    });
  });
};
